/* eslint sort-keys: "error" */
import type { Hotel, Market, Reportgroup } from 'frontend-utils/models';

type Config =
  | {
      flags: string[];
      validate: () => boolean;
    }
  | {
      flags: string[];
      validate: (data?: Hotel | Market) => boolean;
    }
  | {
      flags: string[];
      validate: (data?: Reportgroup) => boolean;
    };

export type Permission =
  | 'app.events'
  | 'app.market.events'
  | 'app.market.parity'
  | 'app.market.rates'
  | 'app.parity'
  | 'app.ranking'
  | 'app.rates'
  | 'events_holidays.author'
  | 'hotel.reports.create.other.existing'
  | 'hotel.reports.create.other.new'
  | 'market.paritymanager.email.hotel.send'
  | 'market.paritymanager.email.wholesaler.send'
  | 'market.paritymanager.files.edit'
  | 'market.paritymanager.issue.edit'
  | 'market.paritymanager.manualissues.add'
  | 'market.reports.create.other.existing'
  | 'report.create.other.existing'
  | 'report.create.other.new'
  | 'report.delete'
  | 'report.edit'
  | 'revenueinsight.pms.edit'
  | 'settings.calendar.colorlimits'
  | 'settings.compset.edit'
  | 'subscription.roomtypemapping.edit';

export const permissionChecks: Record<Permission, Config> = {
  'app.events': {
    flags: ['app.events'],
    validate() {
      return true;
    },
  },
  'app.market.events': {
    flags: ['app.market.events'],
    validate() {
      return true;
    },
  },
  'app.market.parity': {
    flags: ['app.market.parity'],
    validate(hotelOrMarket?: Hotel | Market) {
      const otasParity = hotelOrMarket?.OTAsParity ?? [];

      return otasParity.filter((ota) => ota.isMajor).length > 0;
    },
  },
  'app.market.rates': {
    flags: ['app.market.rates'],
    validate(hotelOrMarket?: Hotel | Market) {
      const otasRates = hotelOrMarket?.OTAsRates ?? [];

      return otasRates.length > 0;
    },
  },
  'app.parity': {
    flags: ['app.parity'],
    validate(hotelOrMarket?: Hotel | Market) {
      const otasParity = hotelOrMarket?.OTAsParity ?? [];

      return otasParity.filter((ota) => ota.isMajor).length > 0;
    },
  },
  'app.ranking': {
    flags: ['app.ranking'],
    validate(hotelOrMarket?: Hotel | Market) {
      const otasRanking = hotelOrMarket?.OTAsRanking ?? [];

      return otasRanking.length > 0;
    },
  },
  'app.rates': {
    flags: ['app.rates'],
    validate(data?: Hotel | Market) {
      const otasRates = data?.OTAsRates ?? [];

      return otasRates.length > 0;
    },
  },
  'events_holidays.author': {
    flags: ['events_holidays.author'],
    validate() {
      return true;
    },
  },
  'hotel.reports.create.other.existing': {
    // Can you create reports for existing users?
    flags: ['reports.create.others'],
    validate() {
      return true;
    },
  },
  'hotel.reports.create.other.new': {
    // Can you create reports for non existing users for this subscription?
    flags: ['reports.create.new_users'],
    validate() {
      return true;
    },
  },
  'market.paritymanager.email.hotel.send': {
    flags: ['market.paritymanager.email.hotel.send'],
    validate() {
      return true;
    },
  },
  'market.paritymanager.email.wholesaler.send': {
    flags: ['market.paritymanager.email.wholesaler.send'],
    validate() {
      return true;
    },
  },
  'market.paritymanager.files.edit': {
    flags: ['market.paritymanager.files.edit'],
    validate() {
      return true;
    },
  },
  'market.paritymanager.issue.edit': {
    flags: ['market.paritymanager.issue.edit'],
    validate() {
      return true;
    },
  },
  'market.paritymanager.manualissues.add': {
    flags: ['market.paritymanager.manualissues.add'],
    validate() {
      return true;
    },
  },
  'market.reports.create.other.existing': {
    // Can you create reports for existing users?
    flags: ['reports.market.create.others'],
    validate() {
      return true;
    },
  },
  'report.create.other.existing': {
    // Can you create reports for existing users?
    flags: [],
    validate(reportgroup?: Reportgroup) {
      return Boolean(reportgroup?.canAddExistingUsers);
    },
  },
  'report.create.other.new': {
    // Can you create reports for non existing users for this reportgroup?
    flags: [],
    validate(reportgroup?: Reportgroup) {
      return Boolean(reportgroup?.canAddNewUsers);
    },
  },
  'report.delete': {
    // Can you delete this reportgroup?
    flags: [],
    validate(reportgroup?: Reportgroup) {
      return Boolean(reportgroup?.canDelete);
    },
  },
  'report.edit': {
    // Can you edit this report
    flags: [],
    validate(reportgroup?: Reportgroup) {
      return Boolean(reportgroup?.canEdit);
    },
  },
  'revenueinsight.pms.edit': {
    flags: ['revenueinsight.pms.edit'],
    validate() {
      return true;
    },
  },
  'settings.calendar.colorlimits': {
    // Can you change the color cut-off percentages on the rates calendar?
    flags: ['settings.calendar.colorlimits'],
    validate() {
      return true;
    },
  },
  'settings.compset.edit': {
    flags: ['settings.compset.edit'],
    validate() {
      return true;
    },
  },
  'subscription.roomtypemapping.edit': {
    flags: ['subscription.roomtypemapping.edit'],
    validate() {
      return true;
    },
  },
};
